/**
 * EmptyState - placeholder for "there is nothing here", with a way out.
 *
 * Mirrors BEETS' component of the same name. An empty table with no
 * explanation is the commonest way a working tool looks broken.
 */

import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import InboxIcon from '@mui/icons-material/Inbox'
import Typography from '@mui/material/Typography'

export interface EmptyStateAction {
  label: string
  onClick?: () => void
  /**
   * Somewhere to go, for an action that leaves the app.
   *
   * A real anchor rather than a scripted navigation, so it can be
   * middle-clicked and reads as a link to a screen reader - the same reason
   * the sidebar's items are anchors. One of onClick or href, not both.
   */
  href?: string
  variant?: 'contained' | 'outlined' | 'text'
  disabled?: boolean
}

export default function EmptyState({
  icon,
  title,
  description,
  actions = [],
}: {
  icon?: ReactNode
  title: string
  description?: string
  actions?: EmptyStateAction[]
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box sx={{ color: 'text.disabled', mb: 2, '& svg': { fontSize: 56 } }}>
        {icon ?? <InboxIcon />}
      </Box>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '52ch', mb: 2 }}>
          {description}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
        {actions.map((action) => (
          <Button
            key={action.label}
            onClick={action.onClick}
            href={action.href}
            variant={action.variant ?? 'contained'}
            disabled={action.disabled}
          >
            {action.label}
          </Button>
        ))}
      </Box>
    </Box>
  )
}
